import React from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowRight,
  ArrowDownRight,
  ArrowUpRight,
  BarChart3,
  ChevronRight,
  Clock,
  CreditCard,
  Eye,
  FileText,
  Heart,
  Inbox,
  Lightbulb,
  Megaphone,
  Users,
} from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { MarketerDashboard } from '@/redux/marketerApi'
import { ago, changeLabel, compact, money, percent } from './format'

// Fixed hex colours, so they read the same whatever the theme does to the Tailwind palette
const INK = {
  green: '#3F7A0A',
  greenFill: '#EEF8DF',
  purple: '#7C3AED',
  purpleFill: '#F1EAFE',
  blue: '#2563EB',
  blueFill: '#E8F0FE',
  orange: '#EA580C',
  orangeFill: '#FDEEE4',
  red: '#DC2626',
  redFill: '#FDECEC',
  amber: '#B45309',
  amberFill: '#FEF3E2',
}

const initials = (name: string | null) =>
  (name ?? '?')
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()

export const Panel: React.FC<{
  title: React.ReactNode
  subtitle?: string
  right?: React.ReactNode
  className?: string
  children: React.ReactNode
}> = ({ title, subtitle, right, className, children }) => (
  <section
    className={cn(
      'rounded-2xl border border-foreground/10 bg-card p-5 sm:p-6 shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
      className,
    )}
  >
    <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h2 className="flex items-center gap-2 text-lg font-bold text-foreground sm:text-xl">
          {title}
        </h2>
        {subtitle ? <p className="mt-1 text-sm text-gray-600">{subtitle}</p> : null}
      </div>
      {right ? <div className="flex items-center gap-3">{right}</div> : null}
    </div>
    {children}
  </section>
)

export const ViewAll: React.FC<{ to: string; label?: string }> = ({ to, label = 'View all' }) => (
  <Link
    to={to}
    className="inline-flex items-center gap-1 text-sm font-semibold text-[#3F7A0A] hover:underline"
  >
    {label} <ArrowRight className="h-4 w-4" />
  </Link>
)

export const PeriodSelect: React.FC<{ days: number; onChange: (d: number) => void }> = ({
  days,
  onChange,
}) => (
  <Select value={String(days)} onValueChange={(v) => onChange(Number(v))}>
    <SelectTrigger className="h-9 w-[140px] border-foreground/15 text-sm" aria-label="Period">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      <SelectItem value="7">Last 7 days</SelectItem>
      <SelectItem value="30">Last 30 days</SelectItem>
      <SelectItem value="90">Last 90 days</SelectItem>
    </SelectContent>
  </Select>
)

const Change: React.FC<{ change: number | null; className?: string }> = ({
  change,
  className,
}) => {
  const down = change !== null && change < 0
  const Icon = down ? ArrowDownRight : ArrowUpRight
  return (
    <span
      className={cn(
        'inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums',
        className,
      )}
      style={{ color: down ? INK.red : INK.green }}
    >
      {change === null ? null : <Icon className="h-3.5 w-3.5" />}
      {changeLabel(change)}
    </span>
  )
}

// ── Stat tiles ────────────────────────────────────────────────────────────────

const Stat: React.FC<{
  to: string
  icon: React.ElementType
  color: string
  fill: string
  label: string
  value: number
  detail: string
  extra?: React.ReactNode
  urgent?: boolean
}> = ({ to, icon: Icon, color, fill, label, value, detail, extra, urgent }) => {
  // In-page anchors (#needs-attention) scroll; everything else is a route
  const Comp: any = to.startsWith('#') ? 'a' : Link
  const target = to.startsWith('#') ? { href: to } : { to }
  return (
    <Comp
      {...target}
      className={cn(
        'group relative flex flex-col rounded-2xl border p-4 transition hover:-translate-y-0.5 hover:shadow-md',
        urgent
          ? 'border-[#F5C9A8] bg-[#FFF7F1]'
          : 'border-foreground/10 bg-card shadow-[0_1px_2px_rgba(30,41,59,0.04)]',
      )}
    >
      <ChevronRight className="absolute right-3 top-4 h-4 w-4 text-gray-600 transition group-hover:translate-x-0.5" />
      <span
        className="flex h-10 w-10 items-center justify-center rounded-xl"
        style={{ background: fill, color }}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span
        className={cn('mt-3 text-sm font-medium', !urgent && 'text-gray-500')}
        style={urgent ? { color: INK.orange } : undefined}
      >
        {label}
      </span>
      <span className="mt-0.5 flex items-baseline gap-2">
        <span className="text-3xl font-bold tabular-nums text-foreground">{value}</span>
        {extra}
      </span>
      <span className="mt-0.5 text-xs text-gray-500">{detail}</span>
    </Comp>
  )
}

export const StatTiles: React.FC<{ data: MarketerDashboard }> = ({ data }) => {
  const s = data.stats
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 xl:grid-cols-5 [&>*:last-child]:col-span-2 md:[&>*:last-child]:col-span-1">
      <Stat
        to="/campaigns"
        icon={Megaphone}
        color={INK.green}
        fill={INK.greenFill}
        label="Active campaigns"
        value={s.activeCampaigns.value}
        detail={`Across ${s.activeCampaigns.brands} brand${s.activeCampaigns.brands === 1 ? '' : 's'}`}
      />
      <Stat
        to="/campaigns?tab=active"
        icon={Users}
        color={INK.blue}
        fill={INK.blueFill}
        label="Active athletes"
        value={s.activeAthletes.value}
        detail="Working on a deal now"
      />
      <Stat
        to="#needs-attention"
        icon={Clock}
        color={INK.orange}
        fill={INK.orangeFill}
        label="Needs your action"
        value={s.needsAction.value}
        detail="Reviews and responses"
        urgent={s.needsAction.value > 0}
      />
      <Stat
        to="/campaigns?tab=active"
        icon={FileText}
        color={INK.green}
        fill={INK.greenFill}
        label="Deals in progress"
        value={s.inProgress.value}
        detail="Awaiting delivery"
      />
      <Stat
        to="#performance"
        icon={BarChart3}
        color={INK.blue}
        fill={INK.blueFill}
        label="Content delivered"
        value={s.contentDelivered.value}
        detail={`Posts in the last ${data.days} days`}
        extra={<Change change={s.contentDelivered.change} />}
      />
    </div>
  )
}

// ── Needs your attention ──────────────────────────────────────────────────────

const PRIORITY: Record<string, { label: string; color: string; fill: string }> = {
  high: { label: 'High', color: INK.red, fill: INK.redFill },
  normal: { label: 'Normal', color: INK.amber, fill: INK.amberFill },
  low: { label: 'Low', color: INK.blue, fill: INK.blueFill },
}

export const NeedsAttention: React.FC<{ data: MarketerDashboard }> = ({ data }) => (
  <Panel
    title={
      <>
        <span id="needs-attention" className="scroll-mt-24">
          Needs your attention
        </span>
        {data.attentionTotal ? (
          <span
            className="inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1.5 text-xs font-bold text-[#fff]"
            style={{ background: INK.red }}
          >
            {data.attentionTotal}
          </span>
        ) : null}
      </>
    }
    right={
      data.attentionTotal > data.attention.length ? <ViewAll to="/campaigns?tab=active" /> : null
    }
  >
    {data.attention.length ? (
      <ul className="divide-y divide-foreground/10">
        {data.attention.map((a) => {
          const p = PRIORITY[a.priority]
          return (
            <li key={a.id}>
              <Link
                to={a.href}
                className="group -mx-2 flex items-center gap-3 rounded-xl px-2 py-3 hover:bg-foreground/10"
              >
                <Avatar className="h-10 w-10 shrink-0">
                  <AvatarImage src={a.athlete.avatar ?? undefined} alt="" />
                  <AvatarFallback className="bg-muted text-xs text-foreground">
                    {initials(a.athlete.name)}
                  </AvatarFallback>
                </Avatar>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-semibold text-foreground">{a.title}</span>
                  <span className="block truncate text-sm text-gray-500">{a.detail}</span>
                </span>
                <span className="hidden shrink-0 rounded-lg bg-[#EEF8DF] px-3 py-1.5 text-sm font-semibold text-[#3F7A0A] sm:inline">
                  {a.action}
                </span>
                <span className="hidden w-14 shrink-0 text-right text-xs text-gray-500 md:inline">
                  {ago(a.at)}
                </span>
                <span
                  className="hidden w-16 shrink-0 rounded-md px-2 py-1 text-center text-xs font-semibold md:inline"
                  style={{ color: p.color, background: p.fill }}
                >
                  {p.label}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-gray-600 transition group-hover:translate-x-0.5" />
              </Link>
            </li>
          )
        })}
      </ul>
    ) : (
      <div className="flex flex-col items-center py-10 text-center">
        <Inbox className="h-9 w-9 text-gray-600" strokeWidth={1.5} />
        <p className="mt-3 font-semibold text-foreground">You're all caught up</p>
        <p className="mt-1 max-w-xs text-sm text-gray-500">
          New applications, submissions to review and steps that are yours will show up here.
        </p>
      </div>
    )}
  </Panel>
)

// ── Top performing athletes ───────────────────────────────────────────────────

const RANK_FILL = ['#FDF3D0', '#EEF1EA', '#FBE6D4', '#EEF1EA', '#EEF1EA']

export const TopAthletes: React.FC<{
  data: MarketerDashboard
  days: number
  onDays: (d: number) => void
}> = ({ data, days, onDays }) => (
  <Panel
    title="Top performing athletes"
    subtitle="Reach from the posts athletes made for your deals."
    right={<PeriodSelect days={days} onChange={onDays} />}
  >
    {data.topAthletes.length ? (
      <ol className="divide-y divide-foreground/10">
        {data.topAthletes.map((a, i) => (
          <li key={a.id}>
            <Link
              to={`/athlete/${a.id}`}
              className="group -mx-2 flex items-center gap-3 rounded-xl px-2 py-3 hover:bg-foreground/10"
            >
              <span
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-foreground"
                style={{ background: RANK_FILL[i] }}
              >
                {i + 1}
              </span>
              <Avatar className="h-10 w-10 shrink-0">
                <AvatarImage src={a.avatar ?? undefined} alt="" />
                <AvatarFallback className="bg-muted text-xs text-foreground">
                  {initials(a.name)}
                </AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-semibold text-foreground">{a.name}</span>
                <span className="block truncate text-sm text-gray-500">
                  {[a.sport, `${a.deals} deal${a.deals === 1 ? '' : 's'}`]
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </span>
              <span className="hidden shrink-0 gap-1.5 md:flex xl:hidden min-[1600px]:flex">
                {a.thumbnails.map((t, j) =>
                  t.url ? (
                    <img
                      key={j}
                      src={t.url}
                      alt=""
                      className="h-11 w-11 rounded-lg object-cover"
                      loading="lazy"
                    />
                  ) : null,
                )}
              </span>
              <span className="w-20 shrink-0 text-right">
                <span className="block font-bold tabular-nums text-foreground">
                  {compact(a.reach)}
                </span>
                <span className="block text-xs text-gray-500">Total reach</span>
              </span>
              <Change change={a.change} className="hidden w-12 justify-end sm:inline-flex" />
              <ChevronRight className="h-4 w-4 shrink-0 text-gray-600 transition group-hover:translate-x-0.5" />
            </Link>
          </li>
        ))}
      </ol>
    ) : (
      <div className="flex flex-col items-center py-10 text-center">
        <Users className="h-9 w-9 text-gray-600" strokeWidth={1.5} />
        <p className="mt-3 font-semibold text-foreground">No posts in this period yet</p>
        <p className="mt-1 max-w-xs text-sm text-gray-500">
          Your top athletes appear once the content from your deals goes live.
        </p>
      </div>
    )}
  </Panel>
)

// ── Performance snapshot ──────────────────────────────────────────────────────

const Sparkline: React.FC<{ values: number[]; color: string; id: string }> = ({
  values,
  color,
  id,
}) => {
  const w = 160
  const h = 56
  const max = Math.max(...values, 0)
  const min = Math.min(...values, 0)
  const span = max - min || 1
  const pts = values.map((v, i) => [
    (i / Math.max(values.length - 1, 1)) * w,
    h - 4 - ((v - min) / span) * (h - 8),
  ])
  const line = pts.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-14 w-full" preserveAspectRatio="none" aria-hidden>
      <defs>
        <linearGradient id={id} x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      {values.length > 1 ? (
        <>
          <path d={`${line} L${w},${h} L0,${h} Z`} fill={`url(#${id})`} />
          <path d={line} fill="none" stroke={color} strokeWidth="2.25" strokeLinejoin="round" />
        </>
      ) : null}
    </svg>
  )
}

const MetricCard: React.FC<{
  id: string
  icon: React.ElementType
  color: string
  fill: string
  label: string
  value: string
  change: number | null
  series: number[]
}> = ({ id, icon: Icon, color, fill, label, value, change, series }) => (
  <div className="flex min-w-0 flex-col rounded-xl border border-foreground/10 p-4">
    <span
      className="flex h-9 w-9 items-center justify-center rounded-lg"
      style={{ background: fill, color }}
    >
      <Icon className="h-[18px] w-[18px]" />
    </span>
    <span className="mt-3 text-sm text-gray-500">{label}</span>
    <span className="mt-1 flex flex-wrap items-baseline gap-x-2">
      <span className="text-2xl font-bold tabular-nums text-foreground">{value}</span>
      <Change change={change} />
    </span>
    <div className="mt-3">
      <Sparkline values={series} color={color} id={`spark-${id}`} />
    </div>
  </div>
)

export const PerformanceSnapshot: React.FC<{
  data: MarketerDashboard
  days: number
  onDays: (d: number) => void
}> = ({ data, days, onDays }) => {
  const s = data.snapshot
  return (
    <Panel
      title={
        <span id="performance" className="scroll-mt-24">
          Performance snapshot
        </span>
      }
      subtitle={`Compared with the ${days} days before.`}
      right={<PeriodSelect days={days} onChange={onDays} />}
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <MetricCard
          id="delivered"
          icon={FileText}
          color={INK.green}
          fill={INK.greenFill}
          label="Content delivered"
          value={String(s.contentDelivered.value)}
          change={s.contentDelivered.change}
          series={s.contentDelivered.series}
        />
        <MetricCard
          id="reach"
          icon={Eye}
          color={INK.purple}
          fill={INK.purpleFill}
          label="Total reach"
          value={compact(s.totalReach.value)}
          change={s.totalReach.change}
          series={s.totalReach.series}
        />
        <MetricCard
          id="engagement"
          icon={Heart}
          color={INK.blue}
          fill={INK.blueFill}
          label="Engagement rate"
          value={percent(s.engagementRate.value)}
          change={s.engagementRate.change}
          series={s.engagementRate.series}
        />
        <MetricCard
          id="spend"
          icon={CreditCard}
          color={INK.orange}
          fill={INK.orangeFill}
          label="Spend to date"
          value={money(s.spendToDate.value)}
          change={s.spendToDate.change}
          series={s.spendToDate.series}
        />
      </div>
    </Panel>
  )
}

// ── Content insights ──────────────────────────────────────────────────────────

export const ContentInsights: React.FC<{ data: MarketerDashboard }> = ({ data }) => {
  const { insights } = data.content
  return (
    <Panel
      title={
        <>
          <Lightbulb className="h-5 w-5" style={{ color: INK.purple }} />
          Content insights
        </>
      }
      subtitle="What's working in your athletes' posts over the last 90 days, by content type."
    >
      {insights.length ? (
        <ul className="space-y-4">
          {insights.map((i) => (
            <li key={i.category} className="flex gap-4">
              {i.topPost.thumbnail ? (
                <img
                  src={i.topPost.thumbnail}
                  alt=""
                  className="h-16 w-16 shrink-0 rounded-xl object-cover sm:h-20 sm:w-20"
                  loading="lazy"
                />
              ) : null}
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-foreground">
                  {i.name} posts get {i.lift.toFixed(1)}x the engagement
                </p>
                <p className="mt-1 text-sm leading-relaxed text-gray-500">
                  {i.posts} post{i.posts === 1 ? '' : 's'} from {i.athletes} athlete
                  {i.athletes === 1 ? '' : 's'} average {percent(i.engagementRate)} engagement,
                  against {percent(i.othersRate)} for the rest of your content.
                  {i.topPost.athlete
                    ? ` Best: ${i.topPost.athlete}'s “${i.topPost.caption}”.`
                    : ''}
                </p>
                <Link
                  to="/campaign/create"
                  className="mt-2 inline-flex items-center gap-1 rounded-full border border-[#C0FD72] px-3 py-1 text-sm font-semibold text-[#3F7A0A] hover:bg-[#EEF8DF]"
                >
                  Brief more like this <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <div className="flex flex-col items-center py-10 text-center">
          <Lightbulb className="h-9 w-9 text-gray-600" strokeWidth={1.5} />
          <p className="mt-3 font-semibold text-foreground">Insights need a few more posts</p>
          <p className="mt-1 max-w-xs text-sm text-gray-500">
            Once athletes post for your deals, we'll show which kinds of content perform best.
          </p>
        </div>
      )}
    </Panel>
  )
}
